import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { DuoMessage, DuoReaction } from '@/types/dinoDuos';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, Smile, Eye, EyeOff } from 'lucide-react';
import { format } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface DuoChatProps {
  duoId: string;
}

const REACTION_EMOJIS = ['⭐', '😱', '🤯', '😍', '😭', '❤️', '🔥', '👏'];

export function DuoChat({ duoId }: DuoChatProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [messages, setMessages] = useState<DuoMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [myCurrentPage, setMyCurrentPage] = useState(0);
  const [revealedSpoilers, setRevealedSpoilers] = useState<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [newMessage]);

  useEffect(() => {
    if (!duoId || !user) return;

    fetchMessages();
    fetchMyProgress();
    setupRealtimeSubscription();
  }, [duoId, user]);

  const fetchMyProgress = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('buddy_read_members')
        .select('current_page')
        .eq('buddy_read_id', duoId)
        .eq('user_id', user.id)
        .single();

      if (error) throw error;

      setMyCurrentPage(data?.current_page || 0);
    } catch (error: any) {
      console.error('Error fetching progress:', error);
    }
  };

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const { data: messagesData, error: messagesError } = await supabase
        .from('duo_messages')
        .select(`
          *,
          user:profiles(id, username, name, image)
        `)
        .eq('duo_id', duoId)
        .order('created_at', { ascending: true });

      if (messagesError) throw messagesError;

      // Fetch reactions for all messages
      const { data: reactionsData, error: reactionsError } = await supabase
        .from('duo_reactions')
        .select(`
          *,
          user:profiles(id, username, name, image)
        `)
        .eq('duo_id', duoId)
        .eq('target_type', 'message');

      if (reactionsError) throw reactionsError;

      // Group reactions by message
      const messageReactions = reactionsData?.reduce((acc: any, reaction: any) => {
        if (!acc[reaction.target_id]) acc[reaction.target_id] = [];
        acc[reaction.target_id].push(reaction);
        return acc;
      }, {});

      setMessages(messagesData?.map((msg: any) => ({
        ...msg,
        user: Array.isArray(msg.user) ? msg.user[0] : msg.user,
        reactions: messageReactions?.[msg.id] || []
      })) || []);
    } catch (error: any) {
      console.error('Error fetching messages:', error);
    }
  };

  const setupRealtimeSubscription = () => {
    const channel = supabase
      .channel(`duo-chat-${duoId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'duo_messages',
          filter: `duo_id=eq.${duoId}`
        },
        () => {
          fetchMessages();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'duo_reactions',
          filter: `duo_id=eq.${duoId}`
        },
        () => {
          fetchMessages();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const handleSendMessage = async () => {
    if (!user || !newMessage.trim() || sending) return;

    setSending(true);
    try {
      const { error } = await supabase
        .from('duo_messages')
        .insert({
          duo_id: duoId,
          user_id: user.id,
          message_text: newMessage.trim(),
          page_at_message: myCurrentPage
        });

      if (error) throw error;

      setNewMessage('');
      await fetchMessages();
    } catch (error: any) {
      console.error('Error sending message:', error);
      toast({
        title: "Failed to send message",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const toggleSpoilerReveal = (messageId: string) => {
    setRevealedSpoilers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const handleReaction = async (messageId: string, emoji: string) => {
    if (!user) return;

    try {
      // Check if user already reacted with this emoji
      const existingReaction = messages
        .find(m => m.id === messageId)
        ?.reactions?.find(r => r.user_id === user.id && r.reaction_emoji === emoji);

      if (existingReaction) {
        // Remove reaction
        await supabase
          .from('duo_reactions')
          .delete()
          .eq('id', existingReaction.id);
      } else {
        // Add reaction
        await supabase
          .from('duo_reactions')
          .insert({
            duo_id: duoId,
            user_id: user.id,
            target_type: 'message',
            target_id: messageId,
            reaction_emoji: emoji
          });
      }
    } catch (error: any) {
      console.error('Error toggling reaction:', error);
      toast({
        title: "Failed to add reaction",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const groupReactionsByEmoji = (reactions: DuoReaction[]) => {
    return reactions.reduce((acc: any, reaction) => {
      if (!acc[reaction.reaction_emoji]) {
        acc[reaction.reaction_emoji] = [];
      }
      acc[reaction.reaction_emoji].push(reaction);
      return acc;
    }, {});
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span>💬</span> Duo Chat
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <ScrollArea className="h-[400px] pr-4">
          <div className="space-y-4">
            {messages.map((message) => {
              const isOwnMessage = message.user_id === user?.id;
              const groupedReactions = groupReactionsByEmoji(message.reactions || []);
              const messagePageNumber = message.page_at_message || 0;
              const isSpoiler = !isOwnMessage && messagePageNumber > myCurrentPage;
              const isRevealed = revealedSpoilers.has(message.id);

              return (
                <div
                  key={message.id}
                  className={`flex gap-3 ${isOwnMessage ? 'flex-row-reverse' : ''}`}
                >
                  <Avatar className="h-8 w-8 flex-shrink-0">
                    <AvatarImage src={message.user?.image} />
                    <AvatarFallback>
                      {message.user?.username?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className={`flex-1 space-y-1 ${isOwnMessage ? 'items-end' : 'items-start'}`}>
                    <div className={`flex items-center gap-2 ${isOwnMessage ? 'flex-row-reverse' : ''}`}>
                      <span className="text-sm font-medium">
                        {message.user?.name || message.user?.username}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(message.created_at), 'HH:mm')}
                      </span>
                    </div>
                    
                    {isSpoiler && !isRevealed ? (
                      <Alert className="max-w-sm">
                        <EyeOff className="h-4 w-4" />
                        <AlertDescription className="flex items-center justify-between gap-2">
                          <span className="text-sm">
                            Spoiler alert! Message from page {messagePageNumber}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleSpoilerReveal(message.id)}
                            className="h-7 text-xs"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Reveal
                          </Button>
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <>
                        <div className="group relative">
                          <div
                            className={`inline-block rounded-lg px-3 py-2 ${
                              isOwnMessage
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted'
                            }`}
                          >
                            <p className="text-sm">{message.message_text}</p>
                            {messagePageNumber > 0 && (
                              <span className="text-xs opacity-60 mt-1 block">
                                Written at page {messagePageNumber}
                              </span>
                            )}
                          </div>
                          
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="absolute -bottom-2 right-0 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <Smile className="h-4 w-4" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-2" align="end">
                              <div className="flex gap-1">
                                {REACTION_EMOJIS.map((emoji) => (
                                  <Button
                                    key={emoji}
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleReaction(message.id, emoji)}
                                    className="h-8 w-8 p-0"
                                  >
                                    {emoji}
                                  </Button>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                        
                        {Object.keys(groupedReactions).length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(groupedReactions).map(([emoji, reactions]: [string, any]) => (
                              <Button
                                key={emoji}
                                variant="outline"
                                size="sm"
                                className="h-6 px-2 text-xs"
                                onClick={() => handleReaction(message.id, emoji)}
                              >
                                {emoji} {reactions.length}
                              </Button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={scrollRef} />
          </div>
        </ScrollArea>

        <div className="flex gap-2 items-end">
          <Textarea
            ref={textareaRef}
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            disabled={sending}
            className="min-h-[40px] max-h-[120px] resize-none"
            rows={1}
          />
          <Button
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || sending}
            size="icon"
            className="flex-shrink-0"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
